import client from "../../auth/client";
import { Auth } from "../../auth/auth";
import type { IContractExecutionHierarchyResponse } from './types';

export const getContractsReview = async (verticalId: string) => {
  const res = await client.get(
    `/doc_review/get_doc_review_data/?vertical_id=${verticalId}`
  );
  return res.data;
};

export const getDocMetaData = async (reqBody: { doc_id: string }) => {
  const res = await client.post("/doc_review/extract_doc_metadata/", reqBody);
  return res.data;
};

export const getVmoBotResponse = async (reqBody: {
  files: string[];
  question: string;
  chat_session_id: string;
}) => {
  const res = await client.post("/doc_review/chat_with_doc/", reqBody);
  return res.data;
};

export const getContractsSyncData = async (reqBody: {
  apiProps: {
    vertical_id: string;
    filter?: [];
  };
  queryParams: {
    page: number;
    page_size: number;
  };
}) => {
  const { page, page_size } = reqBody.queryParams;
  const res = await client.get(
    `/sync_job/list/?page_size=${page_size}&page=${page}`
  );
  return res?.data;
};

export const getContractsSyncDetailsData = async (reqBody: {
  apiProps: {
    vertical_id: string;
    sync_job_id: string;
    filter?: string;
  };
  queryParams: {
    page: number;
    page_size: number;
  };
}) => {
  const { page, page_size } = reqBody.queryParams;
  const { vertical_id, sync_job_id, filter } = reqBody.apiProps;
  const res = await client.get(
    `/sync_job/job_details/?vertical_id=${vertical_id}&sync_job_id=${sync_job_id}&page=${page}&page_size=${page_size}${
      filter ? `&status_filter=${filter}` : ""
    }`
  );
  return res.data;
};

export const getExpiredContracts = async (reqBody: {
  apiProps: {
    vertical_id: string;
    from_date: string;
    to_date: string;
    vendor_name?: string[];
    search_text?: string;
    sort_by?: string[];
    filter?: [];
  };
  queryParams: {
    page: number;
    page_size: number;
  };
}) => {
  delete reqBody.apiProps?.filter;
  const { page, page_size } = reqBody.queryParams;
  const res = await client.post(
    `/dashboard/due_contract/list/?page_size=${page_size}&page=${page}`,
    reqBody.apiProps
  );
  return res?.data;
};

export const getDueContractsDataForGraph = async (reqBody: {
  vertical_id: string;
  from_date: string;
  to_date: string;
}) => {
  const res = await client.post("/dashboard/due_contract/stats/", reqBody);
  return res?.data;
};

export const downloadExpiredContractsCSV = async (reqBody: {
  vertical_id: string;
  from_date: string;
  to_date: string;
}) => {
  const res = await client.post("/dashboard/due_contract/download/", reqBody);
  return res?.data;
};

export const addDocsToConversation = async (reqBody: {
  document_ids: string[];
  chat_session_id?: string;
}) => {
  const res = await client.post("/ttc/add_doc_to_conversation/", reqBody);
  return res.data;
};

export const editAndSaveConversationTitle = async (reqBody: {
  title: string;
  chat_session_id: string;
}) => {
  const res = await client.put("/ttc/edit_title/", reqBody);
  return res.data;
};

export const getChatSessionDetails = async (reqBody: {
  apiProps: {
    search_by?: string;
  };
  queryParams: {
    page: number;
    page_size: number;
  };
}) => {
  const { search_by } = reqBody.apiProps;
  const { page, page_size } = reqBody.queryParams;
  const res = await client.get(
    `/ttc/chat_sessions/?page_size=${page_size}&page=${page}${
      search_by ? `&search_by=${search_by}` : ""
    }`
  );
  return res.data;
};

export const getChatSessionMessages = async (chatSessionId: string) => {
  const res = await client.get(`/ttc/chat_sessions/${chatSessionId}/messages/`);
  return res.data;
};

export const deleteChatHistory = async (chatSessionId: string) => {
  const res = await client.put(`/ttc/chat_session/${chatSessionId}/`);
  return res.data;
};

export const getConversationData = async (chatSessionId: string) => {
  const res = await client.get(`/ttc/chat_sessions/${chatSessionId}/`);
  return res.data;
};

export const getSignedUrlForTTCDocument = async (reqBody: {
  document_id: string;
}) => {
  const res = await client.post("/ttc/get_document_signed_url/", reqBody);
  return res.data;
};

// ============== V2 APIs for Agentic Dashboard ==============

export const getAboutToExpireContractsV2 = async (reqBody: {
  apiProps: {
    vertical_id?: string;
    from_date: string;
    to_date: string;
    vendor_name?: string[];
    search_text?: string;
    sort_by?: string[];
    filter?: [];
  };
  queryParams: {
    page: number;
    page_size: number;
  };
}) => {
  delete reqBody.apiProps?.filter;
  const { page, page_size } = reqBody.queryParams;
  const res = await client.post(
    `/dashboard/due_contract/list/v2/?page_size=${page_size}&page=${page}`,
    reqBody.apiProps
  );
  return res?.data;
};

export const getAboutToExpireContractsDataForGraphV2 = async (reqBody: {
  vertical_id?: string;
  from_date: string;
  to_date: string;
}) => {
  const res = await client.post("/dashboard/due_contract/stats/v2/", reqBody);
  return res?.data;
};

export const downloadAboutToExpireContractsCSVV2 = async (reqBody: {
  vertical_id?: string;
  from_date: string;
  to_date: string;
}) => {
  const res = await client.post("/dashboard/due_contract/download/v2/", {
    ...reqBody,
    access_token: Auth.getInstance().getGoogleAccessToken(),
  });
  return res?.data;
};

// ============== V2 APIs for Expired Contracts ==============

export const getExpiredContractsListV2 = async (reqBody: {
  apiProps: {
    vertical_id?: string;
    from_date: string;
    to_date: string;
    vendor_name?: string[];
    search_text?: string;
    sort_by?: string[];
    filter?: [];
  };
  queryParams: {
    page: number;
    page_size: number;
  };
}) => {
  delete reqBody.apiProps?.filter;
  const { page, page_size } = reqBody.queryParams;
  const res = await client.post(
    `/dashboard/due_contract/get_expired_contracts/v2/?page_size=${page_size}&page=${page}`,
    reqBody.apiProps
  );
  return res?.data;
};

export const getExpiredContractsStatsV2 = async (reqBody: {
  vertical_id?: string;
  from_date: string;
  to_date: string;
}) => {
  const res = await client.post("/dashboard/due_contract/expired_stats/v2/", reqBody);
  return res?.data;
};

export const downloadExpiredContractsListCSVV2 = async (reqBody: {
  vertical_id?: string;
  from_date: string;
  to_date: string;
}) => {
  const res = await client.post("/dashboard/due_contract/expired_download/v2/", {
    ...reqBody,
    access_token: Auth.getInstance().getGoogleAccessToken(),
  });
  return res?.data;
};

export const bulkTerminateContractsV2 = async (reqBody: {
  vertical_id: string;
  contracts: Array<{ rag_file_id: string; comments: string }>;
}) => {
  const res = await client.post("/dashboard/due_contract/bulk_terminate/v2/", reqBody);
  return res?.data;
};

export const bulkRevokeTerminationV2 = async (reqBody: {
  vertical_id: string;
  contracts: Array<{ rag_file_id: string; comments: string }>;
}) => {
  const res = await client.post("/dashboard/due_contract/bulk_revoke_termination/v2/", reqBody);
  return res?.data;
};

// ============== V2 APIs for Terminated Contracts ==============

export const getTerminatedContractsListV2 = async (reqBody: {
  apiProps: {
    vertical_id?: string;
    from_date: string;
    to_date: string;
    vendor_name?: string[];
    search_text?: string;
    sort_by?: string[];
    filter?: [];
  };
  queryParams: {
    page: number;
    page_size: number;
  };
}) => {
  delete reqBody.apiProps?.filter;
  const { page, page_size } = reqBody.queryParams;
  const res = await client.post(
    `/dashboard/due_contract/get_terminated_contracts/v2/?page_size=${page_size}&page=${page}`,
    reqBody.apiProps
  );
  return res?.data;
};

export const getTerminatedContractsStatsV2 = async (reqBody: {
  vertical_id?: string;
  from_date: string;
  to_date: string;
}) => {
  const res = await client.post("/dashboard/due_contract/terminated_stats/v2/", reqBody);
  return res?.data;
};

export const downloadTerminatedContractsListV2 = async (reqBody: {
  vertical_id?: string;
  from_date: string;
  to_date: string;
}) => {
  const res = await client.post("/dashboard/due_contract/terminated_download/v2/", {
    ...reqBody,
    access_token: Auth.getInstance().getGoogleAccessToken(),
  });
  return res?.data;
};

// Sunset Contracts APIs
export const getSunsetContractsListV2 = async (reqBody: {
  apiProps: {
    vertical_id?: string;
    from_date: string;
    to_date: string;
    vendor_name?: string[];
    search_text?: string;
    sort_by?: string[];
    filter?: [];
  };
  queryParams: {
    page: number;
    page_size: number;
  };
}) => {
  delete reqBody.apiProps?.filter;
  const { page, page_size } = reqBody.queryParams;
  const res = await client.post(
    `/dashboard/due_contract/get_sunset_contracts/v2/?page_size=${page_size}&page=${page}`,
    reqBody.apiProps
  );
  return res?.data;
};

export const getSunsetContractsStatsV2 = async (reqBody: {
  vertical_id?: string;
  from_date: string;
  to_date: string;
}) => {
  const res = await client.post("/dashboard/due_contract/sunset_stats/v2/", reqBody);
  return res?.data;
};

export const downloadSunsetContractsListV2 = async (reqBody: {
  vertical_id?: string;
  from_date: string;
  to_date: string;
}) => {
  const res = await client.post("/dashboard/due_contract/sunset_download/v2/", {
    ...reqBody,
    access_token: Auth.getInstance().getGoogleAccessToken(),
  });
  return res?.data;
};

export const bulkSunsetContractsV2 = async (reqBody: {
  vertical_id: string;
  contracts: Array<{ rag_file_id: string; comments: string }>;
}) => {
  const res = await client.post("/dashboard/due_contract/bulk_sunset/v2/", reqBody);
  return res?.data;
};

export const bulkRevokeSunsetV2 = async (reqBody: {
  vertical_id: string;
  contracts: Array<{ rag_file_id: string; comments: string }>;
}) => {
  const res = await client.post("/dashboard/due_contract/bulk_revoke_sunset/v2/", reqBody);
  return res?.data;
};

// ============== Contract Execution Dashboard ==============

export const getContractExecutionHierarchy = async (params: {
  vertical_id?: string | null;
  status?: string;
  search_text?: string;
  agreement_type?: string;
  days_to_expire?: number;
  effective_date_from?: string;
  effective_date_to?: string;
  end_date_from?: string;
  end_date_to?: string;
  page?: number;
  page_size?: number;
  sort_order?: string;
  sort_field?: string;
  hierarchy_mode?: 'sow' | 'msa';
}): Promise<IContractExecutionHierarchyResponse> => {
  const qp = new URLSearchParams();
  if (params.vertical_id) qp.append('vertical_id', params.vertical_id);
  if (params.status) qp.append('status', params.status);
  if (params.search_text) qp.append('search_text', params.search_text);
  if (params.agreement_type) qp.append('agreement_type', params.agreement_type);
  if (params.days_to_expire != null) qp.append('days_to_expire', String(params.days_to_expire));
  if (params.effective_date_from) qp.append('effective_date_from', params.effective_date_from);
  if (params.effective_date_to) qp.append('effective_date_to', params.effective_date_to);
  if (params.end_date_from) qp.append('end_date_from', params.end_date_from);
  if (params.end_date_to) qp.append('end_date_to', params.end_date_to);
  if (params.page) qp.append('page', String(params.page));
  if (params.page_size) qp.append('page_size', String(params.page_size));
  if (params.sort_order) qp.append('sort_order', params.sort_order);
  if (params.sort_field) qp.append('sort_field', params.sort_field);
  if (params.hierarchy_mode) qp.append('hierarchy_mode', params.hierarchy_mode);
  const res = await client.get(`/contract-execution/hierarchy/?${qp.toString()}`);
  return res?.data;
};

// ─── Task Orders — flat one-row-per-task-order listing (task_order_details) ──
export interface ITaskOrderRow {
  rag_file_id?: string | null;
  file_name?: string | null;
  source_url?: string | null;
  source_corpus?: string | null;
  extraction_timestamp?: string | null;
  task_order_name?: string | null;
  bundle?: string | null;
  bundle_number?: string | null;
  application_names?: string | null;
  sow_number?: string | null;
  sow_effective_date?: string | null;
  objective?: string | null;
  scope_of_work?: string | null;
  named_deliverables?: string | null;
  role?: string | null;
  skills_required?: string | null;
  rate_group?: string | null;
  billable_hourly_rate?: string | null;
  total_hc?: number | null;
  locations?: string | null;
  facility_type?: string | null;
  max_total_cost?: string | null;
  service_rate_currency?: string | null;
  invoice_currency?: string | null;
  term_start_end?: string | null;
  contract_duration?: string | null;
  contractor_vendor_name?: string | null;
  google_spe_poc?: string | null;
  delivery_model?: string | null;
  service_level_metric?: string | null;
  engagement_type?: string | null;
  ai_genai_relevance?: string | null;
  internal_approval_status?: string | null;
  signature_status?: string | null;
  open_questions?: string | null;
}

export interface ITaskOrderResponse {
  total: number;
  page: number;
  page_size: number;
  results: ITaskOrderRow[];
}

export const getTaskOrders = async (params: {
  vertical_id?: string;
  search_text?: string;
  page?: number;
  page_size?: number;
  sort_field?: string;
  sort_order?: string;
}): Promise<ITaskOrderResponse> => {
  const qp = new URLSearchParams();
  if (params.vertical_id) qp.append('vertical_id', params.vertical_id);
  if (params.search_text) qp.append('search_text', params.search_text);
  if (params.page) qp.append('page', String(params.page));
  if (params.page_size) qp.append('page_size', String(params.page_size));
  if (params.sort_field) qp.append('sort_field', params.sort_field);
  if (params.sort_order) qp.append('sort_order', params.sort_order);
  const res = await client.get(`/contract-execution/task-orders/?${qp.toString()}`);
  return res?.data;
};

export const getContractExecutionList = async (reqBody: {
  apiProps: {
    vertical_id?: string;
    status?: string;
    search_text?: string;
    supplier_entity?: string[];
    filter?: [];
  };
  queryParams: {
    page: number;
    page_size: number;
  };
}) => {
  const { page, page_size } = reqBody.queryParams;
  const { vertical_id, status, search_text, supplier_entity } = reqBody.apiProps;
  
  const params = new URLSearchParams({
    limit: page_size.toString(),
    offset: ((page - 1) * page_size).toString(),
  });
  
  if (vertical_id) params.append('vertical_id', vertical_id);
  if (status) params.append('status', status);
  if (search_text) params.append('search_text', search_text);
  if (supplier_entity && supplier_entity.length > 0) {
    params.append('supplier_entity', supplier_entity.join(','));
  }
  
  const res = await client.get(`/contract-execution/?${params.toString()}`);
  return res?.data;
};


// ─── Hierarchy override — admin-only manual parent/child mapping ────────
// Backs the "Map orphan" modal in the CRD hierarchy table. Overrides are
// persisted per-vertical and re-applied to the tree on every read; edits
// upsert on (vertical, child_rag_file_id).

export interface IDocSearchResult {
  rag_file_id: string;
  file_name: string | null;
  source_url: string | null;
  vendor_name: string | null;
  document_type: string | null;
  contract_id: string | null;
  document_id: string | null;
  simba_id: string | null;
  vertical_name: string | null;
  rag_corpus_id: string | null;
  /** Whether this result is a logically valid parent/child of the anchor doc
   * passed via `anchor_rag_file_id`, per the contract hierarchy rules.
   * True when no anchor was passed (nothing to check against). */
  can_link_as_parent: boolean;
  can_link_as_child: boolean;
}

/** The fixed document types the RAG pipeline classifies into — mirrors the
 * backend metadata.document_type enum. Used to populate the type filter. */
export const DOCUMENT_TYPE_OPTIONS = [
  'MSA',
  'ISA',
  'MSOW',
  'SOW',
  'Order Form',
  'Amendment',
  'Change Order',
  'Termination',
] as const;

/** Fuzzy-search docs for the override picker. vertical_id can be a UUID or 'all'. */
export const searchDocumentsForMapping = async (
  q: string,
  vertical_id?: string,
  document_type?: string,
  anchor_rag_file_id?: string,
): Promise<{ results: IDocSearchResult[] }> => {
  const qp = new URLSearchParams({ q });
  if (vertical_id) qp.append('vertical_id', vertical_id);
  if (document_type) qp.append('document_type', document_type);
  if (anchor_rag_file_id) qp.append('anchor_rag_file_id', anchor_rag_file_id);
  const res = await client.get(`/contract-execution/documents/search/?${qp.toString()}`);
  return res?.data;
};

/** Create or update a manual child→parent link. Server upserts. */
export const createHierarchyOverride = async (payload: {
  vertical_id: string;
  child_rag_file_id: string;
  parent_rag_file_id: string;
}) => {
  const res = await client.post('/contract-execution/hierarchy-overrides/', payload);
  return res?.data;
};

/** Remove a manual link (auto-linker takes over again). */
export const deleteHierarchyOverride = async (override_id: string) => {
  const res = await client.delete(`/contract-execution/hierarchy-overrides/${override_id}/`);
  return res?.data;
};

/** List existing overrides for a vertical (used to show "manually linked" pills). */
export const listHierarchyOverrides = async (vertical_id: string) => {
  const res = await client.get(
    `/contract-execution/hierarchy-overrides/?vertical_id=${encodeURIComponent(vertical_id)}`,
  );
  return res?.data;
};

